<script>
import { ContentManagementApiService } from "@/services/api/contentManagmentApi";
import { VueEditor } from "@/plugins/VueEditor";

export default {
  components: { VueEditor },
  data() {
    return {
      loading: false,
      editorToolbarConfig: [["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["clean"]],
      textColorTypes: [
        { name: "Black", value: "#000000" },
        { name: "White", value: "#ffffff" },
      ],
      bannerData: null,
      textLimit: 100,
    };
  },
  computed: {
    isFormValid() {
      const { backgroundColor, textColor, title } = this.bannerData;
      return backgroundColor && textColor && title && !this.limitExceeded;
    },
    limitExceeded() {
      return this.bannerData.title?.length > this.textLimit;
    },
  },
  beforeMount() {
    this.getBannerData();
  },
  methods: {
    getBannerData,
    saveBannerData,
    handleTextColorSelect,
    changeBackgroundColor,
  },
};

async function getBannerData() {
  try {
    this.loading = true;
    this.bannerData = await ContentManagementApiService.getSiteWideBanner();
  } catch (error) {
    this.$swal("Attention", error.response.data.error_message);
  } finally {
    this.loading = false;
  }
}

function handleTextColorSelect(item) {
  this.bannerData.textColor = item.value;
}

function changeBackgroundColor(color) {
  this.bannerData.backgroundColor = color;
}

async function saveBannerData() {
  const { title, textColor, backgroundColor, active } = this.bannerData;

  try {
    this.loading = true;
    await ContentManagementApiService.saveSiteWideBanner({
      title,
      active,
      text_color: textColor,
      background_color: backgroundColor,
    });

    this.$swal("Success", "You have successfully saved retailer banner configuration!");
  } catch (error) {
    this.$swal("Attention", error.response.data.error_message);
  } finally {
    this.loading = false;
  }
}
</script>

<style scoped lang="scss">
.page-content {
  padding-inline: 24px;
  .toggle-row {
    .toggle-title {
      font-size: 24px;
      margin-right: 24px;
    }
  }

  .color-selector-row {
    margin-top: 24px;
    .text-color-dropdown {
      width: 33%;
      margin-right: 24px;
    }
    .background-color-picker {
      width: 33%;

      position: relative;
    }
  }

  .mobile-preview {
    &.site-wide-banner {
      text-align: left !important;
      width: 375px;
    }
  }

  .banner-title-counter {
    position: absolute;
    bottom: 25px;
    right: 15px;
  }
}
</style>

<template>
  <div class="nav-collection-editor-page">
    <div class="grid-9 grid-column-gap-0">
      <div class="sidebar-wrapper span-2">
        <slot />
      </div>
      <div class="span-7 span-9-sm page-content">
        <h2 class="font-size-48">Global Components</h2>

        <div v-if="bannerData">
          <div class="toggle-row flex align-center">
            <div class="toggle-title">Retailer Banner</div>
            <toggle v-model="bannerData.active" label="On" class="my-20 fleft" />
          </div>

          <div v-if="bannerData.textColor">
            <h4 class="margin-top-8">DESKTOP PREVIEW</h4>
            <site-wide-banner class="margin-top-8" :banner-data="bannerData" preview></site-wide-banner>
            <h4 class="margin-top-24">MOBILE PREVIEW</h4>
            <site-wide-banner class="margin-top-8 mobile-preview" :banner-data="bannerData" preview></site-wide-banner>
          </div>

          <div class="relative">
            <vue-editor
              class="margin-top-40"
              :editorToolbar="editorToolbarConfig"
              v-model="bannerData.title"
              data-test-id="site_wide_banner_content"
            ></vue-editor>
            <label v-if="bannerData.title" class="banner-title-counter h4 font-size-10"
              ><span :class="{ 'input-error-text': bannerData.title.length > textLimit }">{{
                bannerData.title.length
              }}</span
              ><span class="text-muted">/{{ textLimit }}</span></label
            >

            <div class="font-size-12 input-error-text font-sans margin-top-8" v-if="limitExceeded">
              Copy cannot exceed 100 characters.
            </div>
          </div>

          <div class="color-selector-row flex align-end">
            <div class="text-color-dropdown">
              <div class="margin-bottom-5 b2 overflow-ellipsis text-light">Text color</div>
              <dropdown
                :dropdown-items="textColorTypes"
                :selected-name="textColorTypes.find((item) => item.value === bannerData.textColor).name"
                placeholder="Text color"
                label="Text color"
                :on-select="(item) => handleTextColorSelect(item)"
                :indicator-absolute="true"
              />
            </div>

            <color-picker-input
              class="background-color-picker"
              :color="bannerData.backgroundColor"
              :change-color="changeBackgroundColor"
            >
            </color-picker-input>
          </div>

          <div class="flex justify-end margin-top-24">
            <bulletin-button @click="saveBannerData()" :loading="loading" :disabled="!isFormValid" variant="primary"
              >Save</bulletin-button
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
